import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Swal from '../librarianSwal';
import { useActiveLibraryId, useSectionBase } from '../../Constant/useActiveLibrary';
import Loader from '../../Layout/Loader';
import useMinLoading from '../../Layout/useMinLoading';

import {
  getBookCopiesByLibrary,
  getBookCopiesByBookId,
  updateBookCopyAvailability,
  deleteBookCopy,
  getErrorMessage
} from '../../api/api';
import '../BookList.css';
import '../lib-page-header.css';
import Pagination from '../components/Pagination';

const PAGE_SIZE = 10;

const BookCopyList = () => {
  const navigate = useNavigate();
  const sectionBase = useSectionBase();
  const [searchParams] = useSearchParams();

  const libraryId = useActiveLibraryId();
  const bookId = searchParams.get('book_id');

  const [copies, setCopies] = useState([]);
  const [loading, setLoading] = useState(false);
  const showLoader = useMinLoading(loading);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const fetchCopies = async () => {
    try {
      setLoading(true);

      let data = [];

      if (bookId) {
        data = await getBookCopiesByBookId(bookId);
      } else if (libraryId) {
        data = await getBookCopiesByLibrary(libraryId);
      }

      setCopies(data || []);
    } catch (error) {
      console.error('Fetch book copies error:', error);
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCopies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryId, bookId]);

  const handleAvailabilityChange = async (copy, availability_status) => {
    const confirm = await Swal.fire({
      title: 'Update availability?',
      text: `Copy will be marked as ${availability_status}`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, update',
      cancelButtonText: 'Cancel'
    });

    if (!confirm.isConfirmed) return;

    try {
      await updateBookCopyAvailability(copy.id, availability_status);

      Swal.fire('Success', 'Copy availability updated successfully', 'success');
      fetchCopies();
    } catch (error) {
      console.error('Update availability error:', error);
      Swal.fire('Error', getErrorMessage(error), 'error');
    }
  };

  const handleDelete = async (copy) => {
    const confirm = await Swal.fire({
      title: 'Are you sure?',
      text: 'Issued or reserved copy cannot be deleted.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel'
    });

    if (!confirm.isConfirmed) return;

    try {
      await deleteBookCopy(copy.id);

      Swal.fire('Deleted', 'Book copy deleted successfully', 'success');
      fetchCopies();
    } catch (error) {
      console.error('Delete copy error:', error);
      Swal.fire('Error', getErrorMessage(error), 'error');
    }
  };

  const availabilityColor = (status) => {
    if (status === 'available') return 'green';
    if (status === 'issued') return 'blue';
    if (status === 'reserved') return 'amber';
    if (status === 'lost' || status === 'damaged') return 'red';
    if (status === 'discarded') return 'slate';
    return 'slate';
  };

  const conditionColor = (status) => {
    if (status === 'good') return 'green';
    if (status === 'damaged') return 'red';
    if (status === 'lost') return 'slate';
    return 'slate';
  };

  const filteredCopies = copies.filter((copy) => {
    const keyword = search.toLowerCase();

    return (
      String(copy.title || '').toLowerCase().includes(keyword) ||
      String(copy.book_title || '').toLowerCase().includes(keyword) ||
      String(copy.accession_no || '').toLowerCase().includes(keyword) ||
      String(copy.barcode || '').toLowerCase().includes(keyword) ||
      String(copy.author_name || '').toLowerCase().includes(keyword) ||
      String(copy.availability_status || '').toLowerCase().includes(keyword)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filteredCopies.length / PAGE_SIZE));
  const paginatedCopies = filteredCopies.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  return (
    <div className="book-list-page">

      <div className="lib-page-header">
        <div className="lib-page-header__left">
          <div className="lib-page-header__icon">
            <i className="fas fa-copy" />
          </div>
          <div>
            <h3 className="lib-page-header__title">Book Copies</h3>
            <div className="lib-page-header__subtitle">Manage physical copies, barcode, location and availability</div>
          </div>
        </div>
        <div className="lib-page-header__actions">
          <button
            type="button"
            className="book-add-btn"
            onClick={() =>
              navigate(
                bookId
                  ? `${sectionBase}/book-copies/add?book_id=${bookId}`
                  : `${sectionBase}/book-copies/add`
              )
            }
          >
            <i className="ti ti-plus" /> Add Copy
          </button>
        </div>
      </div>

      {/* ── Filter ── */}
      <div className="book-filter-card">
        <div className="book-filter-row">
          <input
            type="text"
            className="book-search-input"
            placeholder="Search by book, accession no, barcode..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button
            type="button"
            className="book-reset-btn"
            onClick={() => {
              setSearch('');
              fetchCopies();
            }}
          >
            Reset
          </button>
        </div>
      </div>

      {/* ── Table ── */}
      <div className="book-table-card">
        {showLoader ? (
          <Loader />
        ) : (
          <div className="book-table-wrapper">
            <table className="book-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Book</th>
                  <th>Accession No</th>
                  <th>Barcode</th>
                  <th>Copy No</th>
                  <th>Location</th>
                  <th>Purchase</th>
                  <th>Condition</th>
                  <th>Availability</th>
                  <th>Reference</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {paginatedCopies.length > 0 ? (
                  paginatedCopies.map((copy, index) => (
                    <tr key={copy.id}>
                      <td>{(page - 1) * PAGE_SIZE + index + 1}</td>
                      <td>
                        <div className="book-title">{copy.title || copy.book_title || '-'}</div>
                        <div className="book-subtitle">{copy.author_name || '-'}</div>
                      </td>
                      <td>{copy.accession_no || '-'}</td>
                      <td>{copy.barcode || '-'}</td>
                      <td>{copy.copy_number || '-'}</td>
                      <td>
                        <div className="bd-loc-main">Room: {copy.room_no || '-'}</div>
                        <div className="bd-loc-sub">
                          Almirah: {copy.almirah_no || '-'} | Rack: {copy.rack_no || '-'} | Shelf: {copy.shelf_no || '-'}
                        </div>
                        <div className="bd-loc-sub">Row: {copy.row_no || '-'}</div>
                      </td>
                      <td>
                        <div className="bd-loc-main">{copy.purchase_date || '-'}</div>
                        <div className="bd-loc-sub">₹ {copy.purchase_price || 0}</div>
                      </td>
                      <td>
                        <span className={`bd-pill ${conditionColor(copy.condition_status)}`}>
                          {copy.condition_status || '-'}
                        </span>
                      </td>
                      <td>
                        <span className={`bd-pill ${availabilityColor(copy.availability_status)}`}>
                          {copy.availability_status || '-'}
                        </span>
                      </td>
                      <td>
                        <span className={`bd-pill ${copy.is_reference_only ? 'amber' : 'slate'}`}>
                          {copy.is_reference_only ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td>
                        <div className="book-action-buttons">
                          <button
                            className="book-action-btn edit"
                            onClick={() => navigate(`${sectionBase}/book-copies/${copy.id}/edit`)}
                          >
                            <i className="ti ti-edit" /> Edit
                          </button>
                          <button
                            className="book-action-btn success"
                            disabled={copy.availability_status === 'issued'}
                            onClick={() => handleAvailabilityChange(copy, 'available')}
                          >
                            <i className="ti ti-circle-check" /> Available
                          </button>
                          <button
                            className="book-action-btn status"
                            disabled={copy.availability_status === 'issued'}
                            onClick={() => handleAvailabilityChange(copy, 'lost')}
                          >
                            <i className="ti ti-alert-triangle" /> Lost
                          </button>
                          <button
                            className="book-action-btn delete"
                            disabled={
                              copy.availability_status === 'issued' ||
                              copy.availability_status === 'reserved'
                            }
                            onClick={() => handleDelete(copy)}
                          >
                            <i className="ti ti-trash" /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="11" className="book-empty">No book copies found</td>
                  </tr>
                )}
              </tbody>
            </table>

            <Pagination
              page={page}
              totalPages={totalPages}
              totalItems={filteredCopies.length}
              itemLabel="copies"
              onPageChange={setPage}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default BookCopyList;